/**
 * Lightweight analytics façade.
 *
 * No provider is wired up at this layer — the goal is to give the rest
 * of the app a single `track()` call so when a real analytics provider
 * (PostHog / Mixpanel / Segment / GA4) is plugged in, instrumentation
 * doesn't have to change. Today this dispatches via `window.dispatchEvent`
 * and a debug console log; a real provider hooks into the `event` listener.
 *
 * Per FREE_TIER_PLAN.md §6 Phase 7 / §3.3 North Star — these event names
 * are the ones the conversion funnel needs:
 *
 *   tc.signup_completed            { plan, slug }
 *   tc.first_product_published     { slug, productId }
 *   tc.first_storefront_view       { slug }
 *   tc.first_inquiry_received      { slug, orderId }
 *   tc.upgrade_modal_shown         { reason }
 *   tc.upgrade_cta_clicked         { source, fromPlan, targetTier }  // a CTA that skips the modal
 *   tc.upgrade_completed          { fromPlan, toPlan }
 *   tc.inquiry_milestone_hit       { count }   // 10 / 25 / 50
 *
 * Activation signals (fix-list #16) — the server fires tc.order_created,
 * tc.product_published, tc.storefront_published, tc.payments_connected and
 * tc.trial_converted itself; these three are dashboard-only actions:
 *
 *   tc.cut_list_viewed             { order_number, surface, format }  // a cut list was generated/downloaded
 *   tc.build_sheet_exported        { format, surface }                // a CAM nest (DXF or G-code) was exported
 *   tc.dxf_exported                { format: 'dxf', surface }          // the export above, when neutral DXF
 *
 * Report exports:
 *
 *   tc.status_report_pdf           { result, duration_ms }  // success | error | timeout | cancelled
 *
 * Order entry:
 *
 *   tc.new_order_submit            { result, reason, fields, item_count, elapsed_ms }  // saved | invalid | blocked | error
 *
 * Names are namespaced with `tc.` so a real provider's event log stays
 * grep-able for TimberCloud-specific events.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * GA4 KEY EVENTS — a SEPARATE sink, see `trackEvent()` at the bottom.
 *
 * `track()` above is the internal PostHog façade and prefixes everything with
 * `tc.`. The Q0 acquisition plan locks five GA4 key-event names that must
 * arrive UNPREFIXED and must match the Intercom/Loops audience specs verbatim,
 * so they cannot ride the `tc.` bus. They share this module (one place to look
 * for "how do I track something") but deliberately not the same function.
 * ─────────────────────────────────────────────────────────────────────────
 */

import { announceCutListViewed } from '../components/activation/tour/tourEvents';

const EVENT_PREFIX = 'tc.';

/**
 * Fire an analytics event. Safe to call from anywhere — server-side
 * renders and missing window are handled. No-throw by design; analytics
 * should never break the user's flow.
 */
export function track(eventName, properties = {}) {
  if (typeof window === 'undefined') return;
  if (!eventName || typeof eventName !== 'string') return;

  const fullName = eventName.startsWith(EVENT_PREFIX)
    ? eventName
    : `${EVENT_PREFIX}${eventName}`;

  const payload = {
    name: fullName,
    properties: { ...properties, ts: Date.now() },
  };

  try {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[analytics]', fullName, properties);
    }
    // Real providers can listen on this custom event and forward.
    window.dispatchEvent(new CustomEvent('tc:analytics', { detail: payload }));
  } catch (e) {
    // Never throw from analytics
  }
}

// Named convenience wrappers — keeps call sites readable and the event
// names in one place.

export const trackSignupCompleted = (plan, slug) =>
  track('signup_completed', { plan, slug });

export const trackFirstProductPublished = (slug, productId) =>
  track('first_product_published', { slug, productId });

export const trackFirstStorefrontView = (slug) =>
  track('first_storefront_view', { slug });

export const trackFirstInquiryReceived = (slug, orderId) =>
  track('first_inquiry_received', { slug, orderId });

export const trackUpgradeModalShown = (reason) =>
  track('upgrade_modal_shown', { reason });

// For an upgrade CTA that links straight to billing (or the Enterprise inquiry)
// instead of opening UpgradeModal — the modal's open effect is the only other
// place upgrade interest is recorded, so a direct link must report its own.
export const trackUpgradeCtaClicked = (source, fromPlan, targetTier) =>
  track('upgrade_cta_clicked', { source, fromPlan, targetTier });

export const trackUpgradeCompleted = (fromPlan, toPlan) =>
  track('upgrade_completed', { fromPlan, toPlan });

export const trackInquiryMilestoneHit = (count) =>
  track('inquiry_milestone_hit', { count });

// ── Activation signals (fix-list #16) ───────────────────────────────────────
// `surface` is 'order' (a line item's part builder) or 'designer' (the 3D room
// designer). Cut lists have no server endpoint — the PDF/XLSX is built in the
// browser — so the download is the only place to count a "view".

export const trackCutListViewed = ({ orderNumber = null, surface = 'order', format = 'pdf' } = {}) => {
  track('cut_list_viewed', { order_number: orderNumber, surface, format });
  // The trial guide's cut list step (guided tour slice 2): a tour tick, never a trigger.
  announceCutListViewed();
};

// One CAM export = one build_sheet_exported; a neutral-DXF export also counts
// as dxf_exported, so DXF adoption can be read without a format filter.
export const trackBuildSheetExported = ({ format = 'dxf', surface = 'order' } = {}) => {
  track('build_sheet_exported', { format, surface });
  if (format === 'dxf') track('dxf_exported', { format: 'dxf', surface });
};

// ── Report exports ──────────────────────────────────────────────────────────
// One event per Status Report PDF request, so the failure and timeout rates and
// the generation time can be read. 'cancelled' = the modal closed (or a newer
// request replaced this one) before the PDF arrived.
export const trackStatusReportPdf = ({ result, durationMs }) =>
  track('status_report_pdf', { result, duration_ms: durationMs });

// ── Order entry ─────────────────────────────────────────────────────────────
// One event per Save on the new-order page. The server's tc.order_created is
// company-scoped, so it cannot tell a slow form from an abandoned one or show
// why a Save did nothing. 'invalid' = Formik validation failed (`fields` lists
// the top-level field names, never values); 'blocked' = a pre-save guard
// stopped it (`reason`); 'error' = the API rejected it. `elapsed_ms` counts
// from when the page opened.
export const trackNewOrderSubmit = ({ result, reason = null, fields = [], itemCount = 0, elapsedMs }) =>
  track('new_order_submit', { result, reason, fields, item_count: itemCount, elapsed_ms: elapsedMs });

// ───────────────────────────── GA4 key events ─────────────────────────────

/**
 * The GA4 property. gtag is injected by `@next/third-parties`, mounted in the
 * Empty and Centered layouts (see src/layouts/). Both must use THIS id — two
 * ids would split a single visitor's journey across two properties.
 *
 * Note this is a public, client-visible measurement id, not a secret; it lives
 * here rather than in an env var so the two layouts cannot drift apart.
 */
export const GA_MEASUREMENT_ID = 'G-PBVJY55LTP';

const UTM_STORAGE_KEY = 'tc_utms';
// Keep the established storage key for backwards compatibility, but persist
// the complete paid-media attribution payload we need to carry from a landing
// page into signup and the embedded Calendly booking flow.
const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'fbclid',
  'li_fat_id',
  'msclkid',
];

/**
 * First-touch UTM capture.
 *
 * GA4 attributes sessions natively, so this is NOT how the report gets its
 * source — it exists so individual key events carry the campaign that brought
 * the visitor in, and so signup can later hand those values to the app.
 *
 * sessionStorage, NOT a cookie: cookies would drag this into consent scope
 * (see src/consent/), and within-session attribution is all the acquisition
 * plan needs.
 *
 * FIRST-touch: an already-stored set is never overwritten. A visitor who lands
 * from an ad and then clicks an internal link carrying its own utm tags should
 * still be credited to the ad.
 */
export function captureUtms() {
  if (typeof window === 'undefined') return;
  try {
    if (window.sessionStorage.getItem(UTM_STORAGE_KEY)) return;

    const params = new URLSearchParams(window.location.search);
    const found = {};
    UTM_KEYS.forEach((key) => {
      const value = params.get(key);
      if (value) found[key] = value;
    });

    if (Object.keys(found).length === 0) return;
    window.sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(found));
  } catch (e) {
    // Private-mode Safari and storage-blocked browsers throw on access.
    // Losing UTMs must never break the page.
  }
}

/** Read back the stored first-touch UTMs. Always returns an object. */
export function getStoredUtms() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
}

/**
 * Fire a GA4 event with first-touch UTMs attached.
 *
 * Every key event goes through here — no raw `window.gtag('event', …)` calls
 * scattered through components, or the UTM params silently go missing from
 * whichever one got written by hand.
 *
 * Silently no-ops when gtag is absent. That is not a defensive nicety: gtag is
 * mounted per-LAYOUT (Empty + Centered), so a component rendered under
 * Layout1 genuinely has no gtag and must not throw.
 */
export function trackEvent(name, params = {}) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return;
  }
  try {
    window.gtag('event', name, { ...getStoredUtms(), ...params });
  } catch (e) {
    // Never throw from analytics.
  }
}

/**
 * Fire a consent-gated Meta custom event.
 *
 * `window.fbq` only exists after ConsentedScripts has received marketing
 * consent, so this intentionally no-ops when the visitor has not granted that
 * category (or when the Meta script is blocked).
 */
export function trackMetaEvent(name, params = {}) {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
    return;
  }
  try {
    window.fbq('trackCustom', name, { ...getStoredUtms(), ...params });
  } catch (e) {
    // Never throw from advertising instrumentation.
  }
}

const ONCE_STORAGE_PREFIX = 'tc_fired_';

/**
 * `trackEvent`, but at most once per session per name.
 *
 * For `trial_started`: account creation is reachable from three routes
 * (/create-account, /signup/free, /onboarding/free — the last two being the
 * free-tier form and the social/magic-link store-setup step). They are meant
 * to be mutually exclusive per user, but /accounts also pushes into
 * /onboarding/free for a signed-in user with no company, so "exclusive" is a
 * property of current routing rather than something enforced.
 *
 * A key event that silently double-counts halves the reported cost-per-trial,
 * so this does not rely on that routing staying true.
 *
 * Falls through to a plain send if sessionStorage is unavailable — a possible
 * duplicate beats a missing conversion.
 */
export function trackEventOnce(name, params = {}) {
  if (typeof window === 'undefined') return;
  const key = `${ONCE_STORAGE_PREFIX}${name}`;
  try {
    if (window.sessionStorage.getItem(key)) return;
    window.sessionStorage.setItem(key, '1');
  } catch (e) {
    // Storage blocked — send anyway rather than lose the conversion.
  }
  trackEvent(name, params);
}
